import { ContentWrapper, Loader } from 'components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { useMedia } from 'react-use';
import { getShareLink } from 'services/events';
import { sendEmail } from 'services/mail';
import { doGetCommunityMembersByGroups } from 'store/actionCreators/currentCommunity';
import { getSettings } from 'store/selectors/auth';
import {
  getCurrentCommunity,
  getCurrentUserInCommunity,
  getIsMenuOpen,
  getMembersToGroupsLoading,
} from 'store/selectors/currentCommunity';
import { getMyEventsLoaders } from 'store/selectors/events';
import { breakpoints } from 'theme';
import { MyAppointmentsEvent } from 'types';
import { hasCommunityOrGroupAdminPermissions } from 'utils';
import { download } from 'utils/common';
import { toast } from 'utils/toastAbstraction';

import { getMaxCommunityRecipients } from './communityRecipients';
import {
  buildEventEmailBlock,
  composeEmailBody,
  formatEventWhen,
  isHttpUrl,
} from './eventBlock';
import {
  FIELDS,
  getEmptyEmailDraft,
  getFieldError,
  MAX_EMAIL_ATTACHMENTS_BYTES,
  useEmailModuleForm,
} from './form';
import EmailModuleHeader from './Header';
import EmailModuleMessage from './Message';
import { parseOtherRecipients } from './otherRecipients';
import EmailModuleRecipients from './Recipients';
import EmailModuleSelectEvent from './SelectEvent';
import { ContentContainer, GridWrapper, StyledFloatingLoader } from './styled';
import { TurnstileHandle } from './Turnstile';

const EmailModulePageContainer = () => {
  const { t, i18n } = useTranslation();
  const isDownMdPlus = useMedia(breakpoints.downMdPlus);
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const [selectedEvent, setSelectedEvent] = useState<MyAppointmentsEvent>(null);
  const [eventInEmail, setEventInEmail] = useState(false);
  const [eventLink, setEventLink] = useState<string | null>(null);
  const [eventLinkLoading, setEventLinkLoading] = useState(false);
  const [eventLinkFailed, setEventLinkFailed] = useState(false);
  const [composeFullHeight, setComposeFullHeight] = useState(false);
  const [eventResponsesFilter, setEventResponsesFilter] = useState<
    {
      [p: string]: any;
    }[]
  >([]);

  const mailWrapperRef = useRef<HTMLDivElement | null>(null);
  const eventLinkRequest = useRef<{
    id: string;
    promise: Promise<string | null>;
  } | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const community = useSelector(getCurrentCommunity);
  const isMenuOpen = useSelector(getIsMenuOpen);
  const loaders = useSelector(getMyEventsLoaders);
  const currentUser = useSelector(getCurrentUserInCommunity);
  const timeFormat = useSelector(getSettings)?.timeFormat || 24;
  const membersToGroupsLoading = useSelector(getMembersToGroupsLoading);

  useEffect(() => {
    dispatch(
      doGetCommunityMembersByGroups({
        communityId: community.id,
        withoutBlocked: true,
      }),
    );
  }, []);

  useEffect(() => {
    if (!selectedEvent) {
      setEventInEmail(false);
      setEventLink(null);
      setEventLinkFailed(false);
      setEventLinkLoading(false);
      return undefined;
    }
    if (!eventInEmail) return undefined;

    const direct = isHttpUrl(selectedEvent.link)
      ? selectedEvent.link.trim()
      : '';
    if (direct) {
      setEventLink(direct);
      setEventLinkFailed(false);
      setEventLinkLoading(false);
      return undefined;
    }
    if (!community?.id) return undefined;

    let cancelled = false;
    setEventLink(null);
    setEventLinkFailed(false);
    setEventLinkLoading(true);
    const promise: Promise<string | null> = getShareLink(
      community.id,
      selectedEvent.id,
    )
      .then((data) => {
        const url = data?.shareLink?.shortLink;
        if (!isHttpUrl(url)) {
          if (!cancelled) setEventLinkFailed(true);
          return null;
        }
        const link = url.trim();
        if (!cancelled) setEventLink(link);
        return link;
      })
      .catch(() => {
        if (!cancelled) setEventLinkFailed(true);
        return null;
      })
      .finally(() => {
        if (!cancelled) setEventLinkLoading(false);
      });
    eventLinkRequest.current = { id: selectedEvent.id, promise };

    return () => {
      cancelled = true;
    };
  }, [selectedEvent, eventInEmail, community?.id]);

  useEffect(() => {
    const modalRefVal = mailWrapperRef.current;

    const handleOutSideClick = (event: MouseEvent) => {
      if (modalRefVal && modalRefVal.contains(event.target as Node)) {
        event.preventDefault();
        event.stopPropagation();
        addToast(t('errors.onlyForGroupAndAdmin'), {
          appearance: 'info',
          autoDismiss: true,
        });
      }
    };

    if (!hasCommunityOrGroupAdminPermissions(currentUser.id, community)) {
      document.addEventListener('mousedown', handleOutSideClick, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutSideClick);
    };
  }, []);

  const defaultValues = useMemo(() => getEmptyEmailDraft(), []);

  const maxCommunityRecipients = getMaxCommunityRecipients(community?.users);

  const {
    errors,
    setError,
    clearErrors,
    handleSubmit,
    register,
    reset,
    setValue,
    trigger,
    formState: { isValid },
    values,
    attachmentsFieldProps,
    recipientsFieldProps,
    sendToMeFieldProps,
    otherRecipientsFieldProps,
    messageFieldProps,
  } = useEmailModuleForm(defaultValues, eventInEmail);

  const eventInEmailRef = useRef(eventInEmail);
  const skipMessageValidationRef = useRef(false);
  useEffect(() => {
    if (eventInEmailRef.current === eventInEmail) return;
    eventInEmailRef.current = eventInEmail;
    if (skipMessageValidationRef.current) {
      skipMessageValidationRef.current = false;
      return;
    }
    trigger(FIELDS.MESSAGE);
  }, [eventInEmail, trigger]);

  const getTotalAttachmentBytes = (files: { size?: number }[]) =>
    files.reduce((sum, f) => sum + (f?.size ?? 0), 0);

  const handleRecipientsChange = (value: string[]): void => {
    const selectedUsers = value
      .map((id) => community.users.find((u) => u.id === id)?.id)
      .filter((i) => i);

    recipientsFieldProps.onChange(
      selectedUsers.slice(0, maxCommunityRecipients),
    );
    if (selectedUsers.length > 0) {
      clearErrors(FIELDS.RECIPIENTS);
    }
  };

  const eventWhen = selectedEvent
    ? formatEventWhen(selectedEvent, timeFormat, t, i18n.language)
    : '';
  const eventWhere = selectedEvent?.location?.trim() || '';
  const eventBlockHtml =
    eventInEmail && selectedEvent
      ? buildEventEmailBlock({
          title: selectedEvent.title,
          when: eventWhen,
          where: eventWhere,
          url: eventLink,
          viewLabel: t('email.viewEvent'),
          accent: selectedEvent.eventType?.color,
        })
      : '';

  const submitForm = (): Promise<boolean> => {
    if (!hasCommunityOrGroupAdminPermissions(currentUser.id ?? '', community)) {
      return Promise.resolve(false);
    }
    setSending(true);

    return new Promise((resolve) => {
      handleSubmit(
        async (data) => {
          const recipients = data.recipients
            .map(
              (id) => community.users.find((u) => u.id === id)?.profile?.email,
            )
            .filter((i) => !!i);
          try {
            let block = '';
            if (eventInEmail && selectedEvent) {
              let url = isHttpUrl(selectedEvent.link)
                ? selectedEvent.link.trim()
                : eventLink;
              const pending = eventLinkRequest.current;
              if (!isHttpUrl(url) && pending?.id === selectedEvent.id) {
                url = await pending.promise;
              }
              block = buildEventEmailBlock({
                title: selectedEvent.title,
                when: eventWhen,
                where: eventWhere,
                url,
                viewLabel: t('email.viewEvent'),
                accent: selectedEvent.eventType?.color,
              });
            }

            await sendEmail(
              community.id,
              {
                ...data,
                recipients,
                [FIELDS.MESSAGE]: composeEmailBody(block, data[FIELDS.MESSAGE]),
              },
              FIELDS,
              captchaToken,
            );

            setCaptchaToken(null);
            turnstileRef.current?.reset();

            const recipientCount =
              recipients.length +
              parseOtherRecipients(data[FIELDS.OTHER_RECIPIENTS]).length;

            toast(
              {
                title: t(
                  recipientCount === 1
                    ? 'email.emailSent.title_one'
                    : 'email.emailSent.title_other',
                  { count: recipientCount },
                ),
                message: t('email.emailSent.message'),
              },
              {
                appearance: 'success',
                autoDismiss: false,
              },
            );
            resolve(true);
          } catch {
            setCaptchaToken(null);
            turnstileRef.current?.reset();
            addToast(t('email.sendNotCompleted'), {
              appearance: 'info',
              autoDismiss: true,
            });
            resolve(false);
          } finally {
            setSending(false);
          }
        },
        (submitErrors) => {
          setSending(false);
          const fieldOrder = [
            FIELDS.RECIPIENTS,
            FIELDS.SUBJECT,
            FIELDS.MESSAGE,
            FIELDS.OTHER_RECIPIENTS,
            FIELDS.ATTACHMENTS,
          ];
          const firstMessage = fieldOrder
            .map((field) => getFieldError(submitErrors, field)?.message)
            .find(Boolean);
          if (firstMessage) {
            addToast(firstMessage, {
              appearance: 'error',
              autoDismiss: true,
            });
          }
          resolve(false);
        },
      )();
    });
  };

  const handleDocumentsAttach = async (data) => {
    const newFiles = await Promise.all(
      data
        .filter(
          (i) => !values[FIELDS.ATTACHMENTS].some((f) => f.name === i.name),
        )
        .map((i) => download(i.url, i.name, i.mime)),
    );

    const merged = [...values[FIELDS.ATTACHMENTS], ...newFiles];

    if (getTotalAttachmentBytes(merged) > MAX_EMAIL_ATTACHMENTS_BYTES) {
      setError(FIELDS.ATTACHMENTS, {
        type: 'validate',
        message: t('email.maxAttachmentsSize'),
      });
      return;
    }

    clearErrors(FIELDS.ATTACHMENTS);
    attachmentsFieldProps.onChange(merged);
  };

  const handleRemoveDocument = (document: any) => {
    attachmentsFieldProps.onChange(
      values[FIELDS.ATTACHMENTS].filter((i) => i.name !== document.name),
    );
  };

  const handleSendToMeChange = (checked: boolean) => {
    sendToMeFieldProps.onChange(checked);
  };

  const handleDiscardDraft = () => {
    if (eventInEmail) skipMessageValidationRef.current = true;
    eventLinkRequest.current = null;
    setSelectedEvent(null);
    setEventInEmail(false);
    setEventResponsesFilter([]);
    reset(getEmptyEmailDraft());
    addToast(t('email.draftDiscarded'), {
      appearance: 'info',
      autoDismiss: true,
    });
  };

  const getRecipientsFilteredByReplies = (
    selectedEvent: MyAppointmentsEvent,
    currentRecipients: string[],
    filters: { [p: string]: any },
  ) => {
    if (!hasCommunityOrGroupAdminPermissions(currentUser.id ?? '', community)) {
      return;
    }
    const recipients = selectedEvent.recipients;
    const isAwaiting = filters.find((i) => i.value === 'awaiting');
    const isPenalty = filters.find((i) => i.value === 'penalty');
    const isAttended = filters.find((i) => i.value === 'attended');
    const eventReplies = selectedEvent.subEventReplies;

    let users = [];

    if (filters.length === 0) {
      return [...new Set(currentRecipients.concat(selectedEvent.recipients))];
    }

    if (isAwaiting) {
      const usersWithoutReplies = recipients.filter(
        (id) => !eventReplies.some((r) => r.userId === id),
      );

      users = users.concat(usersWithoutReplies);
    }

    if (isPenalty) {
      const usersWithPenalty = eventReplies
        .filter((r) => r.penalty)
        .map((r) => r.userId);

      users = users.concat(usersWithPenalty);
    }

    if (isAttended) {
      const usersWithAttended = eventReplies
        .filter((r) => r.attended)
        .map((r) => r.userId);

      users = users.concat(usersWithAttended);
    }

    const eventRepliesIds = filters
      .filter((i) => !['penalty', 'attended', 'awaiting'].includes(i.value))
      .map((i) => i.value);

    if (eventRepliesIds.length) {
      const otherUsers = eventReplies
        .filter((r) => eventRepliesIds.includes(r.eventResponseId))
        .map((r) => r.userId);

      users = users.concat(otherUsers);
    }

    const uniqueRecipients = [...new Set(users)];
    const recipientsToRemove = recipients.filter(
      (id) => !uniqueRecipients.includes(id),
    );

    return [...new Set(currentRecipients.concat(uniqueRecipients))].filter(
      (id) => !recipientsToRemove.includes(id),
    );
  };

  const handleEventClick = (event: MyAppointmentsEvent) => {
    if (!hasCommunityOrGroupAdminPermissions(currentUser.id ?? '', community)) {
      return;
    }
    if (event.id === selectedEvent?.id) {
      handleRecipientsChange(
        values[FIELDS.RECIPIENTS].filter(
          (id) => !event.recipients.includes(id),
        ),
      );
      setValue(
        FIELDS.SUBJECT,
        values[FIELDS.SUBJECT].replace(`${event.title}: `, ''),
      );

      setEventResponsesFilter([]);
      return setSelectedEvent(null);
    }

    const defaultAllFilterValue = community.eventResponses.map((response) => ({
      value: response.id,
      label: response.title,
    }));

    setEventResponsesFilter(
      [
        { value: 'awaiting', label: t('event.awaiting') },
        { value: 'penalty', label: t('event.penalty') },
        { value: 'attended', label: t('event.nomination') },
      ].concat(defaultAllFilterValue),
    );

    const updatedRecipients = getRecipientsFilteredByReplies(
      event,
      values[FIELDS.RECIPIENTS],
      eventResponsesFilter,
    );

    handleRecipientsChange(updatedRecipients);

    if (selectedEvent) {
      setValue(
        FIELDS.SUBJECT,
        `${values[FIELDS.SUBJECT].replace(
          `${selectedEvent.title}`,
          `${event.title}`,
        )}`,
      );
    } else {
      setValue(FIELDS.SUBJECT, `${event.title}: ${values[FIELDS.SUBJECT]}`);
    }

    setSelectedEvent(event);
  };

  const handleEventResponsesChange = (responses: { [p: string]: any }[]) => {
    const updatedRecipients = getRecipientsFilteredByReplies(
      selectedEvent,
      values[FIELDS.RECIPIENTS],
      responses,
    );

    setEventResponsesFilter(responses);
    handleRecipientsChange(updatedRecipients);
  };

  const handleOnWrapperClick = (event) => {
    event?.stopPropagation();
    event?.preventDefault();
  };

  if (!community || !currentUser) {
    return <Loader />;
  }

  return (
    <ContentWrapper isMenuOpen={isMenuOpen} minHeight={'auto'}>
      {loaders.events && (
        <StyledFloatingLoader>
          <Loader size="60px" type={null} />
        </StyledFloatingLoader>
      )}
      <EmailModuleHeader isMenuOpen={isMenuOpen} />
      <GridWrapper ref={mailWrapperRef}>
        {!isDownMdPlus ? (
          <EmailModuleRecipients
            currentUserId={currentUser.id ?? ''}
            handleOnWrapperClick={handleOnWrapperClick}
            community={community}
            selectedRecipients={values[FIELDS.RECIPIENTS]}
            membersToGroupsLoading={membersToGroupsLoading}
            membersByGroups={community.membersByGroups}
            users={community.users}
            onChange={handleRecipientsChange}
            error={getFieldError(errors, FIELDS.RECIPIENTS)}
          />
        ) : null}
        <ContentContainer>
          <EmailModuleSelectEvent
            currentUserId={currentUser.id ?? ''}
            handleOnWrapperClick={handleOnWrapperClick}
            selectedEvent={selectedEvent}
            eventResponsesFilter={eventResponsesFilter}
            community={community}
            isLoading={loaders.events}
            isMenuOpen={isMenuOpen}
            onEventResponsesChange={handleEventResponsesChange}
            onEventClick={handleEventClick}
            hidden={composeFullHeight}
          />
          <EmailModuleMessage
            handleOnWrapperClick={handleOnWrapperClick}
            attachmentsFieldProps={attachmentsFieldProps}
            messageFieldProps={messageFieldProps}
            selectedRecipients={values[FIELDS.RECIPIENTS]}
            otherRecipients={values[FIELDS.OTHER_RECIPIENTS]}
            subject={values[FIELDS.SUBJECT]}
            outgoingBody={composeEmailBody(
              eventBlockHtml,
              values[FIELDS.MESSAGE] || '',
            )}
            selectedEvent={selectedEvent}
            eventInEmail={eventInEmail}
            eventWhen={eventWhen}
            eventWhere={eventWhere}
            eventLink={eventLink}
            eventLinkLoading={eventLinkLoading}
            eventLinkFailed={eventLinkFailed}
            onAddEventToEmail={() => setEventInEmail(true)}
            onRemoveEventFromEmail={() => setEventInEmail(false)}
            setSelectedParticipants={handleRecipientsChange}
            errors={errors}
            setError={setError}
            clearErrors={clearErrors}
            trigger={trigger}
            otherRecipientsFieldProps={otherRecipientsFieldProps}
            sending={sending}
            sendDisabled={sending || !isValid}
            register={register}
            documentation={community.documentation?.documents}
            sendToMe={values[FIELDS.SEND_TO_ME]}
            onSendToMeChange={handleSendToMeChange}
            onRemoveDocument={handleRemoveDocument}
            onDocumentsAttach={handleDocumentsAttach}
            submitForm={submitForm}
            onDiscardDraft={handleDiscardDraft}
            captchaToken={captchaToken}
            onCaptchaSuccess={setCaptchaToken}
            onCaptchaExpire={() => setCaptchaToken(null)}
            turnstileRef={turnstileRef}
            fullHeight={composeFullHeight}
            onFullHeightChange={setComposeFullHeight}
          />
        </ContentContainer>
      </GridWrapper>
    </ContentWrapper>
  );
};

export default EmailModulePageContainer;
